/** Formats seconds as `m:ss` or `h:mm:ss`; optional tenths for precise UI readouts. */
export function formatTimecode(seconds: number, withTenths = false): string {
  const safe = Number.isFinite(seconds) && seconds > 0 ? seconds : 0;
  const totalTenths = Math.floor(safe * 10 + 1e-6);
  const tenths = totalTenths % 10;
  const totalSeconds = Math.floor(totalTenths / 10);
  const s = totalSeconds % 60;
  const m = Math.floor(totalSeconds / 60) % 60;
  const h = Math.floor(totalSeconds / 3600);
  const pad = (n: number) => String(n).padStart(2, '0');
  const base = h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
  return withTenths ? `${base}.${tenths}` : base;
}

/** Human readable byte size using locale-aware number formatting. */
export function formatBytes(bytes: number, locale?: string): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const exponent = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const value = bytes / 1024 ** exponent;
  const formatted = new Intl.NumberFormat(locale, { maximumFractionDigits: value < 10 && exponent > 0 ? 1 : 0 }).format(value);
  return `${formatted} ${units[exponent]}`;
}

/** A unit's word: one form, or a form for one and a form for any other count ("minute", "minutes"). */
export type LengthUnit = string | { readonly one: string; readonly other: string };

/** Unit words for {@link formatLength}: abbreviations ("min") or words ("minutes"). */
export interface LengthUnits {
  readonly h: LengthUnit;
  readonly min: LengthUnit;
  readonly s: LengthUnit;
}

const unitWord = (unit: LengthUnit, count: number): string => (typeof unit === 'string' ? unit : count === 1 ? unit.one : unit.other);

/**
 * A length in the two largest units that matter ("45 s", "2 min 5 s",
 * "1 h 12 min"), whole seconds rounded down; under a second it keeps its
 * tenths ("0.5 s"), since some limits start there.
 */
export function formatLength(seconds: number, units: LengthUnits, locale?: string): string {
  const safe = Number.isFinite(seconds) && seconds > 0 ? seconds : 0;
  if (safe > 0 && safe < 1) return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(safe)} ${unitWord(units.s, safe)}`;
  const whole = Math.floor(safe);
  const [h, min, s] = [Math.floor(whole / 3600), Math.floor((whole % 3600) / 60), whole % 60];
  const part = (count: number, unit: LengthUnit) => `${count} ${unitWord(unit, count)}`;
  if (h) return min ? `${part(h, units.h)} ${part(min, units.min)}` : part(h, units.h);
  if (min) return s ? `${part(min, units.min)} ${part(s, units.s)}` : part(min, units.min);
  return part(s, units.s);
}
